import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";

interface ElectronAPI {
  fetchCookies: (communityUrl: string, authToken: string) => Promise<string[]>;
  openUrl: (url: string, cookies: Array<any>) => Promise<boolean>;
  logout: () => void;
  setAuthTokenToMain: (authToken: string) => Promise<void>;
  switchCommunity: (communityData: any) => Promise<boolean>;
  changeCommunity: (data: any) => void;
  setNotificationCount: (count: number) => void;
  setUnreadMessageCount: (count: number) => void;
  sendNotification: (data: any) => void;
  testNotification: () => void;
}

const MAX_RETRIES = 3;
const RETRY_INTERVAL_MS = 500;

interface UseElectronAPIOutput {
  electronAPI: ElectronAPI | null;
  isLoading: boolean;
  error: string | null;
  isElectron: boolean;
}

/**
 * Custom React hook to get the electronAPI object from the window,
 * with retries if not immediately available.
 */
export const useElectronAPI = (): UseElectronAPIOutput => {
  const [api, setApi] = useState<ElectronAPI | null>(null);
  const [isElectron, setIsElectron] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const findAndSetApi = (retriesLeft: number) => {
      if (!isMounted) {
        return; // Component unmounted, stop.
      }

      const electronAPIInstance = window.webview?.electronAPI;

      if (!isEmpty(electronAPIInstance)) {
        setApi(electronAPIInstance as ElectronAPI);
        setIsElectron(true);
        setIsLoading(false);
        setError(null);
        return; // API found, stop.
      }

      if (retriesLeft > 0) {
        timeoutId = setTimeout(
          () => findAndSetApi(retriesLeft - 1),
          RETRY_INTERVAL_MS,
        );
      } else {
        // No retries left, API not found.
        console.error("Electron API not found by hook after maximum retries.");
        setError("Electron API not available.");
        setIsLoading(false);
      }
    };

    findAndSetApi(MAX_RETRIES);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return { electronAPI: api, isLoading, error, isElectron };
};
